import { Box, Card, Group, SimpleGrid, Text, ThemeIcon } from '@mantine/core';
import {
  IconEye,
  IconMessages,
  IconTargetArrow,
  IconTrendingDown,
  IconTrendingUp,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export interface KpiStripProps {
  totalViews: number;
  totalMessages: number;
  conversionRate: number | null;
  messagesTrendPct: number | null;
  viewsTrendPct: number | null;
  conversionTrendPct: number | null;
}

const numberFmt = new Intl.NumberFormat();

// Card body uses position: relative so the corner blob (which is absolutely
// positioned) anchors against the card itself instead of escaping it.
const cardBaseStyle = { position: 'relative' as const, overflow: 'hidden' as const };
const cardEmphasisStyle = {
  ...cardBaseStyle,
  borderColor: 'var(--mantine-color-primary-filled)',
  borderWidth: 2,
};

function safeNumber(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

function TrendRow({ pct, accent }: { pct: number | null; accent?: 'primary' | 'default' }) {
  const { t } = useTranslation();
  if (pct === null || !Number.isFinite(pct)) {
    return null;
  }
  const positive = pct >= 0;
  const Icon = positive ? IconTrendingUp : IconTrendingDown;
  const color = accent === 'primary' ? 'primary' : positive ? 'teal' : 'red';
  const sign = positive ? '+' : '';
  return (
    <Group gap={6} mt={6} wrap="nowrap">
      <Icon size={16} color={`var(--mantine-color-${color}-6)`} />
      <Text size="xs" fw={500} c={color}>
        {sign}
        {pct.toFixed(1)}%
      </Text>
      <Text size="xs" c="dimmed">
        {t('messages.vsLastWeek')}
      </Text>
    </Group>
  );
}

interface KpiCardProps {
  label: string;
  icon: React.ReactNode;
  value: string;
  emphasis?: boolean;
  trendPct: number | null;
}

function KpiCard({ label, icon, value, emphasis, trendPct }: KpiCardProps) {
  return (
    <Card withBorder padding="lg" radius="md" style={emphasis ? cardEmphasisStyle : cardBaseStyle}>
      {emphasis && (
        <Box
          style={{
            position: 'absolute',
            top: 0,
            insetInlineEnd: 0,
            width: 128,
            height: 128,
            background: 'var(--mantine-color-primary-light)',
            opacity: 0.35,
            borderRadius: '0 12px 0 100px',
            pointerEvents: 'none',
          }}
        />
      )}
      <Group
        justify="space-between"
        align="flex-start"
        wrap="nowrap"
        style={{ position: 'relative' }}
      >
        <Text size="sm" c="dimmed" fw={500}>
          {label}
        </Text>
        <ThemeIcon size={32} radius="xl" variant="light" color={emphasis ? 'primary' : 'gray'}>
          {icon}
        </ThemeIcon>
      </Group>
      <Text
        fz={48}
        fw={700}
        lh={1.1}
        mt="md"
        c={emphasis ? 'primary' : undefined}
        style={{ position: 'relative' }}
      >
        {value}
      </Text>
      <Box style={{ position: 'relative' }}>
        <TrendRow pct={trendPct} accent={emphasis ? 'primary' : 'default'} />
      </Box>
    </Card>
  );
}

export function KpiStrip(props: KpiStripProps) {
  const { t } = useTranslation();
  const conversionDisplay =
    props.conversionRate === null || !Number.isFinite(props.conversionRate)
      ? t('messages.conversionNa')
      : `${props.conversionRate.toFixed(1)}%`;

  return (
    <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
      <KpiCard
        label={t('accountPages.totalViews')}
        icon={<IconEye size={18} />}
        value={numberFmt.format(safeNumber(props.totalViews))}
        trendPct={props.viewsTrendPct}
      />
      <KpiCard
        label={t('accountPages.totalMessages')}
        icon={<IconMessages size={18} />}
        value={numberFmt.format(safeNumber(props.totalMessages))}
        trendPct={props.messagesTrendPct}
      />
      <KpiCard
        label={t('messages.conversionRate')}
        icon={<IconTargetArrow size={18} />}
        value={conversionDisplay}
        emphasis={props.conversionRate !== null && Number.isFinite(props.conversionRate)}
        trendPct={props.conversionTrendPct}
      />
    </SimpleGrid>
  );
}
